import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, Copy, Truck, AlertTriangle } from "lucide-react";

const STATUS_LABEL = {
  held: "ESCROW · HELD",
  pending_manual: "PENDING",
  paid: "PAID",
  failed: "FAILED",
};

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [filter, setFilter] = useState("held");
  const [refNo, setRefNo] = useState({});

  const load = useCallback(() => {
    api.get("/admin/payouts", { params: filter === "all" ? {} : { status: filter } })
      .then(({ data }) => setPayouts(data))
      .catch((err) => console.warn("payouts fetch failed", err));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  const markPaid = async (payout_id) => {
    if (!window.confirm("Release this payout to the seller? Do this only AFTER you've transferred the money via UPI/bank.")) return;
    try {
      await api.post(`/admin/payouts/${payout_id}/mark-paid`, {
        reference: refNo[payout_id] || null,
        notes: null,
      });
      toast.success("Payout released");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const totalHeld = payouts.filter((p) => p.status === "held").reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div data-testid="admin-payouts">
      <div className="border-2 border-black bg-[#F4F4F0] p-3 mb-4 font-mono-b text-xs">
        <b>ESCROW FLOW</b> — Money is held with OUTLAW-D until the seller uploads shipping proof (AWB + package photo). Once the proof is up, the payout can be released. Buyer pays → held → seller ships → admin releases.
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {[
          ["held", "HELD (ESCROW)"],
          ["paid", "RELEASED"],
          ["all", "ALL"],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`payout-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-2 border-2 border-black font-mono-b font-bold uppercase text-xs ${filter === k ? "bg-black text-[#D4FF00]" : "bg-white"}`}
          >
            {l}
          </button>
        ))}
        {filter === "held" && (
          <span className="ml-auto font-mono-b text-sm">
            Total in escrow: <b className="text-[#FF007F]">₹{totalHeld.toFixed(2)}</b>
          </span>
        )}
      </div>

      {payouts.length === 0 ? (
        <p className="font-mono-b p-8 text-center border-2 border-dashed border-black" data-testid="payouts-empty">No payouts here.</p>
      ) : (
        <div className="space-y-3">
          {payouts.map((p) => (
            <div key={p.payout_id} data-testid={`payout-${p.payout_id}`} className="bg-white border-2 border-black brutal-shadow-sm p-4">
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <p className="font-mono-b text-xs text-black/60">{p.payout_id} · Order {p.order_id?.slice(0, 16)}</p>
                  <p className="font-display text-2xl">₹{p.amount} <span className={`text-sm ${p.status === "paid" ? "text-green-700" : p.status === "failed" ? "text-red-600" : "text-[#FF007F]"}`}>· {STATUS_LABEL[p.status] || p.status.toUpperCase()}</span></p>
                  <p className="font-mono-b text-xs mt-1"><b>{p.seller_name}</b> · {p.seller_email}</p>
                </div>
                <p className="font-mono-b text-xs">{new Date(p.created_at).toLocaleString()}</p>
              </div>

              {/* Shipping proof status */}
              <div className={`mt-3 border-2 border-black p-3 font-mono-b text-xs flex items-center gap-2 flex-wrap ${p.has_shipping_proof ? "bg-[#D4FF00]" : "bg-[#FF007F] text-white"}`} data-testid={`payout-shipping-${p.payout_id}`}>
                {p.has_shipping_proof ? (
                  <>
                    <Truck className="w-4 h-4" />
                    <span><b>SHIPPED</b> · {p.courier} · {p.awb}</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    <span><b>NO SHIPPING PROOF YET</b> — seller must upload AWB + package photo before you can release.</span>
                  </>
                )}
              </div>

              {/* Payout method details */}
              <div className="mt-3 bg-[#F4F4F0] border-2 border-black p-3 font-mono-b text-sm" data-testid={`payout-method-${p.payout_id}`}>
                {!p.payout_details?.method ? (
                  <p className="text-black/70">Seller hasn't added payout details yet. Ping them at <b>{p.seller_email}</b>.</p>
                ) : p.payout_details.method === "upi" ? (
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span><b>UPI:</b> {p.payout_details.vpa}</span>
                    <button onClick={() => copy(p.payout_details.vpa)} className="border-2 border-black bg-white px-2 py-1 flex items-center gap-1 text-xs uppercase font-bold hover-nudge" data-testid={`copy-${p.payout_id}`}>
                      <Copy className="w-3 h-3" /> COPY
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex justify-between"><b>NAME:</b> <span>{p.payout_details.account_holder_name}</span></div>
                    <div className="flex justify-between items-center gap-2 flex-wrap">
                      <span><b>A/C:</b> {p.payout_details.account_number}</span>
                      <button onClick={() => copy(p.payout_details.account_number)} className="border-2 border-black bg-white px-2 py-0.5 flex items-center gap-1 text-xs uppercase font-bold"><Copy className="w-3 h-3" /> COPY</button>
                    </div>
                    <div className="flex justify-between items-center gap-2 flex-wrap">
                      <span><b>IFSC:</b> {p.payout_details.ifsc}</span>
                      <button onClick={() => copy(p.payout_details.ifsc)} className="border-2 border-black bg-white px-2 py-0.5 flex items-center gap-1 text-xs uppercase font-bold"><Copy className="w-3 h-3" /> COPY</button>
                    </div>
                  </div>
                )}
              </div>

              {p.status === "held" && p.payout_details?.method && p.has_shipping_proof && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <input
                    data-testid={`payout-ref-${p.payout_id}`}
                    value={refNo[p.payout_id] || ""}
                    onChange={(e) => setRefNo({ ...refNo, [p.payout_id]: e.target.value })}
                    placeholder="UPI/Bank reference no. (optional)"
                    className="brutal-input flex-1 text-sm"
                  />
                  <button
                    data-testid={`mark-paid-${p.payout_id}`}
                    onClick={() => markPaid(p.payout_id)}
                    className="bg-[#D4FF00] border-2 border-black px-4 py-2 font-mono-b font-bold uppercase text-sm brutal-shadow-sm hover-nudge flex items-center gap-1"
                  >
                    <Check className="w-4 h-4" /> RELEASE PAYOUT
                  </button>
                </div>
              )}

              {p.status === "paid" && p.reference && (
                <p className="mt-2 font-mono-b text-xs text-black/60">Ref: {p.reference}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
